import { useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { AccountMenu, AppShell, Button, NavGroup, NavItem, SidebarNav, ThemeToggle } from '@sectepe/ui';
import { useAuth } from './contexts/AuthContext';
import { setTargetTenant } from './services/api';
import LifecyclePage from './pages/LifecyclePage';
import AccessRequestsPage from './pages/AccessRequestsPage';
import AccessReviewsPage from './pages/AccessReviewsPage';
import AttestationPage from './pages/AttestationPage';
import SodPoliciesPage from './pages/SodPoliciesPage';
import AuditPage from './pages/AuditPage';
import UsersPage from './pages/UsersPage';
import GroupsPage from './pages/GroupsPage';
import RolesPage from './pages/RolesPage';
import PimPage from './pages/PimPage';
import GuestsPage from './pages/GuestsPage';
import RiskPage from './pages/RiskPage';
import ConditionalAccessPage from './pages/ConditionalAccessPage';

// `manage: true` items are available to operators with iga-admin AND to
// tenant-admins (who carry no IGA realm role but are group-gated server-side).
const NAV = [
  { to: '/users', label: 'Benutzer', roles: ['iga-admin'], manage: true },
  { to: '/groups', label: 'Gruppen', roles: ['iga-admin'], manage: true },
  { to: '/roles', label: 'Rollen & Rechte', roles: ['iga-admin'], manage: true },
  { to: '/lifecycle', label: 'Lifecycle', roles: ['iga-admin'] },
  { to: '/access-requests', label: 'Access-Requests', roles: ['iga-admin', 'iga-reviewer', 'iga-auditor'] },
  { to: '/pim', label: 'Privileged Access (PIM)', roles: ['pim-eligible', 'pim-approver', 'iga-admin'], manage: true },
  { to: '/guests', label: 'Gastzugänge', roles: ['iga-admin', 'iga-reviewer', 'iga-auditor', 'guest-sponsor'], manage: true },
  { to: '/risk', label: 'Anmelderisiken', roles: ['iga-admin', 'iga-reviewer', 'iga-auditor'], manage: true },
  { to: '/conditional-access', label: 'Bedingter Zugriff', roles: ['iga-admin', 'iga-reviewer', 'iga-auditor'], manage: true },
  { to: '/access-reviews', label: 'Access-Reviews', roles: ['iga-admin', 'iga-reviewer', 'iga-auditor'] },
  { to: '/attestation', label: 'Attestation', roles: ['iga-admin', 'iga-reviewer', 'iga-auditor'] },
  { to: '/sod', label: 'SoD-Policies', roles: ['iga-admin', 'iga-reviewer', 'iga-auditor'] },
  { to: '/audit', label: 'Audit & Reports', roles: ['iga-admin', 'iga-auditor'] },
];

function SignIn() {
  const { signIn } = useAuth();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6">
      <div className="text-center">
        <h1 className="font-brand text-2xl">SecTepe.IGA</h1>
        <p className="mt-1 text-sm text-muted">Identity Governance &amp; Administration</p>
      </div>
      <button className="btn-primary" onClick={signIn}>
        Anmelden
      </button>
    </div>
  );
}

export default function App() {
  const { me, loading, signOut, hasRole } = useAuth();
  const [tenantInput, setTenantInput] = useState('');
  const [appliedTenant, setAppliedTenant] = useState('');
  const [open, setOpen] = useState(false);

  if (loading) {
    return <div className="flex h-full items-center justify-center text-muted">Lädt …</div>;
  }
  if (!me) return <SignIn />;

  const isTenantAdmin = me.capability === 'tenant-admin';
  const isOperator = me.capability === 'operator';
  const visibleNav = NAV.filter((n) =>
    n.manage ? hasRole(...n.roles) || isTenantAdmin : hasRole(...n.roles)
  );

  const applyTenant = () => {
    setTargetTenant(tenantInput || null);
    setAppliedTenant(tenantInput);
  };

  return (
    <AppShell
      brand={
        <>
          <span className="font-brand text-base">SecTepe.IGA</span>
          {me.tenant && <span className="badge">tenant: {me.tenant}</span>}
        </>
      }
      sidebarOpen={open}
      onSidebarToggle={setOpen}
      menuLabel="Menü"
      actions={
        <>
          {isOperator && (
            <div className="hidden items-center gap-1 md:flex">
              <span className="text-xs text-muted">Mandant:</span>
              <input
                className="input input-sm w-32"
                placeholder="alle / slug"
                value={tenantInput}
                onChange={(e) => setTenantInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyTenant()}
              />
              <Button variant="ghost" size="sm" onClick={applyTenant}>
                Setzen
              </Button>
            </div>
          )}
          <ThemeToggle />
        </>
      }
      profile={
        <AccountMenu
          label={me.username || me.email}
          secondary={
            isTenantAdmin
              ? 'Tenant-Admin'
              : me.roles.filter((r) => r.startsWith('iga-')).join(', ') || 'keine IGA-Rolle'
          }
          onLogout={signOut}
          logoutLabel="Abmelden"
        />
      }
      sidebar={
        <SidebarNav>
          <NavGroup>
            {visibleNav.map((n) => (
              <NavItem key={n.to} as={NavLink} to={n.to} onClick={() => setOpen(false)}>
                {n.label}
              </NavItem>
            ))}
          </NavGroup>
        </SidebarNav>
      }
    >
        <div className="p-4 lg:p-6" key={appliedTenant}>
          <Routes>
            <Route path="/users" element={<UsersPage />} />
            <Route path="/groups" element={<GroupsPage />} />
            <Route path="/roles" element={<RolesPage />} />
            <Route path="/lifecycle" element={<LifecyclePage />} />
            <Route path="/access-requests" element={<AccessRequestsPage />} />
            <Route path="/pim" element={<PimPage />} />
            <Route path="/guests" element={<GuestsPage />} />
            <Route path="/risk" element={<RiskPage />} />
            <Route path="/conditional-access" element={<ConditionalAccessPage />} />
            <Route path="/access-reviews" element={<AccessReviewsPage />} />
            <Route path="/attestation" element={<AttestationPage />} />
            <Route path="/sod" element={<SodPoliciesPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="*" element={<Navigate to={visibleNav[0]?.to ?? '/access-requests'} replace />} />
          </Routes>
        </div>
    </AppShell>
  );
}
